import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
    Check,
    ChevronDown,
    ExternalLink,
    LogOut,
    Plus,
    Save,
    Trash2,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { getRemoteMenu, isMenuAdmin, saveRemoteMenu } from "../../lib/menuService";
import { menuData as localMenuData } from "../../components/menu/menuData";
import buscavidaText from "../../assets/images/busca-vida-text.png";
import "./Admin.css";
import Seo from "../../components/Seo";

const cloneMenu = (menu) => JSON.parse(JSON.stringify(menu));

const slugify = (value) =>
    value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "") || `nuovo-${Date.now()}`;

const formatUpdatedAt = (value) => {
    if (!value) return "Non ancora pubblicato";

    return new Intl.DateTimeFormat("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
    }).format(new Date(value));
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [menu, setMenu] = useState([]);
    const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
    const [isChecking, setIsChecking] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [notice, setNotice] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);

    const loadDashboard = useCallback(async () => {
        const { data: sessionData } = await supabase.auth.getSession();
        const currentUser = sessionData.session?.user;

        if (!currentUser) {
            setAccessDenied(true);
            setIsChecking(false);
            return;
        }

        try {
            const allowed = await isMenuAdmin();

            if (!allowed) {
                await supabase.auth.signOut();
                setAccessDenied(true);
                setIsChecking(false);
                return;
            }

            const remote = await getRemoteMenu();
            const hasPublishedMenu = remote.content.length > 0;

            setUser(currentUser);
            setMenu(cloneMenu(hasPublishedMenu ? remote.content : localMenuData));
            setUpdatedAt(remote.updatedAt);
            setIsDirty(!hasPublishedMenu);

            if (!hasPublishedMenu) {
                setNotice({
                    type: "info",
                    text: "Il menu attuale è pronto per il primo salvataggio su Supabase.",
                });
            }
        } catch {
            setNotice({
                type: "error",
                text: "Non riesco a caricare il menu. Controlla la connessione e riprova.",
            });
        } finally {
            setIsChecking(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const activeCategory = menu[activeCategoryIndex];

    const totalItems = useMemo(
        () =>
            menu.reduce(
                (total, category) =>
                    total +
                    category.sections.reduce(
                        (sectionTotal, section) => sectionTotal + section.items.length,
                        0
                    ),
                0
            ),
        [menu]
    );

    const updateMenu = (updater) => {
        setMenu((current) => {
            const next = cloneMenu(current);
            updater(next);
            return next;
        });
        setIsDirty(true);
        setNotice(null);
    };

    const updateCategory = (field, value) => {
        updateMenu((next) => {
            next[activeCategoryIndex][field] = value;
        });
    };

    const updateSection = (sectionIndex, field, value) => {
        updateMenu((next) => {
            next[activeCategoryIndex].sections[sectionIndex][field] = value;
        });
    };

    const updateItem = (sectionIndex, itemIndex, field, value) => {
        updateMenu((next) => {
            const item = next[activeCategoryIndex].sections[sectionIndex].items[itemIndex];
            item[field] =
                field === "tags"
                    ? value.split(",").map((tag) => tag.trim()).filter(Boolean)
                    : value;
        });
    };

    const addCategory = () => {
        const title = "Nuova categoria";
        updateMenu((next) => {
            next.push({
                id: `${slugify(title)}-${Date.now()}`,
                title,
                sections: [],
            });
        });
        setActiveCategoryIndex(menu.length);
    };

    const removeCategory = () => {
        if (!window.confirm("Vuoi eliminare questa categoria e tutti i suoi piatti?")) return;

        updateMenu((next) => {
            next.splice(activeCategoryIndex, 1);
        });
        setActiveCategoryIndex((current) => Math.max(0, current - 1));
    };

    const addSection = () => {
        updateMenu((next) => {
            next[activeCategoryIndex].sections.push({
                id: `nuova-sezione-${Date.now()}`,
                title: "Nuova sezione",
                items: [],
            });
        });
    };

    const removeSection = (sectionIndex) => {
        if (!window.confirm("Vuoi eliminare questa sezione e tutti i suoi piatti?")) return;

        updateMenu((next) => {
            next[activeCategoryIndex].sections.splice(sectionIndex, 1);
        });
    };

    const addItem = (sectionIndex) => {
        updateMenu((next) => {
            next[activeCategoryIndex].sections[sectionIndex].items.push({
                name: "Nuovo piatto",
                description: "",
                price: "",
                tags: [],
            });
        });
    };

    const removeItem = (sectionIndex, itemIndex) => {
        if (!window.confirm("Vuoi eliminare questo piatto?")) return;

        updateMenu((next) => {
            next[activeCategoryIndex].sections[sectionIndex].items.splice(itemIndex, 1);
        });
    };

    const handleSave = async () => {
        if (!user || !isDirty) return;

        setIsSaving(true);
        setNotice(null);

        try {
            const result = await saveRemoteMenu(menu, user.id);
            setUpdatedAt(result.updated_at);
            setIsDirty(false);
            setNotice({ type: "success", text: "Menu pubblicato correttamente." });
        } catch {
            setNotice({
                type: "error",
                text: "Il salvataggio non è riuscito. Riprova tra poco.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate("/admin/login", { replace: true });
    };

    if (isChecking) {
        return <div className="admin-loading">Caricamento menu…</div>;
    }

    if (accessDenied) {
        return <Navigate to="/admin/login" replace />;
    }

    return (
        <main className="admin-dashboard">
            <Seo
                title="Gestione menu | Busca Vida"
                description="Area riservata agli amministratori di Busca Vida."
                path="/admin"
                noIndex
            />
            <header className="admin-topbar">
                <img src={buscavidaText} alt="Busca Vida" />
                <div className="admin-topbar-actions">
                    <a href="/menu" target="_blank" rel="noreferrer">
                        Vedi menu <ExternalLink size={16} />
                    </a>
                    <button type="button" onClick={handleLogout}>
                        Esci <LogOut size={16} />
                    </button>
                </div>
            </header>

            <div className="admin-shell">
                <section className="admin-heading">
                    <div>
                        <p className="admin-eyebrow">Area amministratore</p>
                        <h1>Gestione menu</h1>
                        <p>
                            {totalItems} piatti · Ultimo aggiornamento: {formatUpdatedAt(updatedAt)}
                        </p>
                    </div>
                    <button
                        className="admin-primary-button admin-save-button"
                        type="button"
                        onClick={handleSave}
                        disabled={!isDirty || isSaving}
                    >
                        {isSaving ? <span className="admin-spinner" /> : isDirty ? <Save size={18} /> : <Check size={18} />}
                        {isSaving ? "Salvataggio…" : isDirty ? "Salva e pubblica" : "Salvato"}
                    </button>
                </section>

                {notice && (
                    <div className={`admin-notice ${notice.type}`} role="status">
                        {notice.text}
                    </div>
                )}

                <section className="admin-category-bar">
                    <div className="admin-category-tabs">
                        {menu.map((category, index) => (
                            <button
                                type="button"
                                key={category.id}
                                className={index === activeCategoryIndex ? "active" : ""}
                                onClick={() => setActiveCategoryIndex(index)}
                            >
                                {category.title || category.sections[0]?.title || category.id}
                            </button>
                        ))}
                    </div>
                    <button type="button" className="admin-add-category" onClick={addCategory}>
                        <Plus size={17} /> Categoria
                    </button>
                </section>

                {activeCategory && (
                    <section className="admin-editor">
                        <div className="admin-category-editor">
                            <label>
                                Nome categoria
                                <input
                                    value={activeCategory.title}
                                    placeholder="Es. Pizzeria"
                                    onChange={(event) => updateCategory("title", event.target.value)}
                                />
                            </label>
                            <button type="button" className="admin-danger-link" onClick={removeCategory}>
                                <Trash2 size={17} /> Elimina categoria
                            </button>
                        </div>

                        {activeCategory.sections.map((section, sectionIndex) => (
                            <details className="admin-section-card" key={section.id} open>
                                <summary>
                                    <span>
                                        <ChevronDown size={19} />
                                        {section.title || "Sezione senza nome"}
                                    </span>
                                    <small>{section.items.length} piatti</small>
                                </summary>

                                <div className="admin-section-content">
                                    <div className="admin-section-toolbar">
                                        <label>
                                            Nome sezione
                                            <input
                                                value={section.title}
                                                onChange={(event) =>
                                                    updateSection(sectionIndex, "title", event.target.value)
                                                }
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className="admin-danger-link"
                                            onClick={() => removeSection(sectionIndex)}
                                        >
                                            <Trash2 size={16} /> Elimina sezione
                                        </button>
                                    </div>

                                    <div className="admin-items-list">
                                        {section.items.map((item, itemIndex) => (
                                            <article className="admin-item-card" key={`${section.id}-${itemIndex}`}>
                                                <div className="admin-item-main">
                                                    <label>
                                                        Nome piatto
                                                        <input
                                                            value={item.name}
                                                            onChange={(event) =>
                                                                updateItem(sectionIndex, itemIndex, "name", event.target.value)
                                                            }
                                                        />
                                                    </label>
                                                    <label className="admin-price-field">
                                                        Prezzo
                                                        <input
                                                            value={item.price}
                                                            placeholder="9,00 €"
                                                            onChange={(event) =>
                                                                updateItem(sectionIndex, itemIndex, "price", event.target.value)
                                                            }
                                                        />
                                                    </label>
                                                </div>
                                                <label>
                                                    Descrizione
                                                    <textarea
                                                        value={item.description}
                                                        rows="2"
                                                        onChange={(event) =>
                                                            updateItem(sectionIndex, itemIndex, "description", event.target.value)
                                                        }
                                                    />
                                                </label>
                                                <div className="admin-item-footer">
                                                    <label>
                                                        Tag separati da virgola
                                                        <input
                                                            value={(item.tags || []).join(", ")}
                                                            placeholder="vegetariana, piccante"
                                                            onChange={(event) =>
                                                                updateItem(sectionIndex, itemIndex, "tags", event.target.value)
                                                            }
                                                        />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className="admin-delete-icon"
                                                        onClick={() => removeItem(sectionIndex, itemIndex)}
                                                        aria-label={`Elimina ${item.name}`}
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </article>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        className="admin-outline-button"
                                        onClick={() => addItem(sectionIndex)}
                                    >
                                        <Plus size={18} /> Aggiungi piatto
                                    </button>
                                </div>
                            </details>
                        ))}

                        <button type="button" className="admin-outline-button admin-add-section" onClick={addSection}>
                            <Plus size={18} /> Aggiungi sezione
                        </button>
                    </section>
                )}
            </div>
        </main>
    );
}
